import {
  cloneListOfCloneable, DataModelBuilder, isSameData, isSameList,
  SerializationContext, SerializedData,
} from '../../dto/dto.js';
import { FeeRange } from '../fee-range.js';
import { MapUtils } from '../../../utility/map-utils.js';
import { ApplicationFeeProtocol } from './application-fee-protocol.js';
import { Price } from '../price.js';
import { Fee } from '../fee.js';
import { InvoiceVat } from '../invoicing/invoice-vat.js';
import { VersionRange } from '../../core/version-range.js';
import { fromEnumAsStringOrNull, toEnumFromStringOrNull } from '../../../serialization/enum-utils.js';

export class DynamicApplicationFeeProtocol extends ApplicationFeeProtocol {
  static readonly TYPE_ID: string = 'afbbeba3d3e74552b14e18d28d9120ca';
  static readonly SCHEMA_VERSION: number = 3;

  constructor(
    public readonly feeRanges: FeeRange[],
    public readonly maxFee: Price | null,
    public readonly overrideVat: InvoiceVat | null,
  ) {
    super();

    //  Fee ranges are sorted in ascending order so we can calculate the unit price fee.
    feeRanges.sort((a, b) => a.priceRange.from.value - b.priceRange.from.value);
  }

  get $removable(): boolean {
    return false;
  }

  get $typeId(): string {
    return DynamicApplicationFeeProtocol.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, DynamicApplicationFeeProtocol.SCHEMA_VERSION);
  }

  static fromMap(context: SerializationContext, map: SerializedData): DynamicApplicationFeeProtocol {
    return new DynamicApplicationFeeProtocol(
        MapUtils.getList<FeeRange>(map, 'feeRanges', (e) => {
          return context.fromData(e as SerializedData);
        }),
        Price.decodeOrNull(MapUtils.getOrNull<string>(map, 'maxFee')),
        toEnumFromStringOrNull(MapUtils.getStringOrNull(map, 'overrideVat'), InvoiceVat),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'feeRanges': this.feeRanges.map((e) => context.toData(e)),
      'maxFee': this.maxFee?.encode() ?? null,
      'overrideVat': fromEnumAsStringOrNull(this.overrideVat),
    };
  }

  clone(): this {
    return new DynamicApplicationFeeProtocol(
        cloneListOfCloneable(this.feeRanges),
        this.maxFee?.clone() ?? null,
        this.overrideVat,
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return isSameList(other.feeRanges, this.feeRanges, (a, b) => isSameData(a, b)) &&
        isSameData(other.maxFee, this.maxFee) &&
        other.overrideVat == this.overrideVat;
  }

  getUnitFee(unitPrice: Price): Fee | null {
    for(let i = this.feeRanges.length - 1; i >= 0; i--) {
      if(unitPrice.value >= this.feeRanges[i].priceRange.from.value) {
        return this.feeRanges[i].fee.clone();
      }
    }

    return null;
  }

  calculateUnitFee(unitPrice: Price): Price {
    let unitFee = Price.zero(unitPrice.currency);
    for(let i = this.feeRanges.length - 1; i >= 0; i--) {
      if(unitPrice.value >= this.feeRanges[i].priceRange.from.value) {
        unitFee = this.feeRanges[i].fee.calculateUnitFee(unitPrice);
        break;
      }
    }

    if(this.maxFee != null && this.maxFee.value > 0 && unitFee.compareTo(this.maxFee) > 0) {
      unitFee = this.maxFee.clone();
    }

    return unitFee;
  }
}

export class DynamicApplicationFeeProtocolBuilder extends DataModelBuilder<DynamicApplicationFeeProtocol> {
  build(context: SerializationContext, map: SerializedData): DynamicApplicationFeeProtocol {
    return DynamicApplicationFeeProtocol.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(DynamicApplicationFeeProtocol.SCHEMA_VERSION)) {
      return map;
    }

    if(versionRange.isGreaterThan(DynamicApplicationFeeProtocol.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In this version `maxFee` of type `Price | null` was introduced.
    if(version < 2) {
      version = 2;
    }

    // In this version parameter `overrideVat` of type `InvoiceVat | null` was introduced.
    if(version < 3) {
      version = 3;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
